import { Text, Box, Flex, useDisclosure, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  finleyAssert,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  Util,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { useSendFinancialCovenantWorklease } from '../hooks';
import {
  AddIconButton,
  SaveIconButton,
  ResetIconButton,
  IconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import {
  CreditAgreement,
  Bank,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { AddFinancialCovenantModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/financialCovenant/AddFinancialCovenantModal';
import { FinancialCovenantTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/financialCovenant/FinancialCovenantTable';

const createFinancialCovenantPropertyShell = (params: {
  nameInput: string;
  covenantKeyInput: string;
}): CreditAgreement.DomainEntity.FinancialCovenantProperty => {
  const { covenantKeyInput, nameInput } = params;
  return {
    name: nameInput,
    covenantKey: covenantKeyInput,
    type: CreditAgreement.DomainEntity.PropertyType.FinancialCovenantProperty,
    value: _.startCase(covenantKeyInput),
    _isWrittenIn_: true,
    // TODO: should deprecate and remove these eventually, not being shown anymore
    limit: {
      value: 0,
      valueType:
        CreditAgreement.Config.FinancialCovenant.LimitValueType.currency,
      currency: Bank.Currency.USD,
    },
    limitType: CreditAgreement.Config.FinancialCovenant.LimitType.Min,
  };
};

type FinancialCovenantsProps = {
  organizationId: string;
  financialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  wipConfig?: WipConfig;
};
export const FinancialCovenants = ({
  organizationId,
  financialCovenants,
  wipConfig,
}: FinancialCovenantsProps) => {
  const basePropertyKey = 'financialCovenants';
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toast = useToast();

  const [triggerDate, setTriggerDate] = useState<Util.FullDateStr>(
    commonDate.dateToFullDateString(new Date(), clientTimeZone),
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    sendFinancialCovenantWorklease,
    isSendFinCovWorkleaseLoading,
    evaluateDevFinCovWorklease,
    isEvaluateDevFinCovWorkleaseLoading,
  } = useSendFinancialCovenantWorklease();

  const addFinancialCovenant = (params: {
    nameInput: string;
    covenantKeyInput: string;
  }) => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      onClose();
      return;
    }

    const currentFinancialCovenants = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isFinancialCovenantProperties(
        currentFinancialCovenants,
      ),
      'Expected financial covenant properties structure while adding new one',
    );

    const { covenantKeyInput } = params;
    const covenantKeyExists = currentFinancialCovenants.some(
      (finCov) => finCov.covenantKey === covenantKeyInput,
    );
    if (covenantKeyExists) {
      toast({
        title: `Covenant Key "${covenantKeyInput}" already exists, please configure unique one`,
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
      return;
    }

    const newFinancialCovenant = createFinancialCovenantPropertyShell(params);
    wipConfig.formProps.setFieldValue(basePropertyKey, [
      ...currentFinancialCovenants,
      newFinancialCovenant,
    ]);

    onClose();
  };

  if (!wipConfig && !_.compact(Object.values(financialCovenants)).length) {
    return null;
  }

  const isLenderWipOperationMode =
    wipConfig && wipConfig.operationMode === 'lender';

  return (
    <Box mt="40px" mb="35px">
      <StickyHeaderSection labelText="Financial Covenants">
        {wipConfig?.isMutateAllowed(basePropertyKey) &&
          !isLenderWipOperationMode && (
            <Flex
              alignItems="center"
              border="1px"
              borderColor="gray.700"
              p="3"
              mb="5"
            >
              <Text fontStyle="italic" mr="3">
                &quot;triggerDate&quot; mock:
              </Text>
              <SimpleDatePicker
                selectedDate={commonDate.fullDateStrToDate(triggerDate)}
                setSelectedDate={(date: Date) => {
                  const fullDateStr = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  setTriggerDate(fullDateStr);
                }}
              />
              <SaveIconButton
                ariaLabel="save financial covenants"
                label="Save Financial Covenants"
                onClick={() => {
                  wipConfig.saveCreditAgreementFullArtifactWip({
                    variables: {
                      creditAgreementFullArtifactWip: {
                        id: wipConfig.formProps.values.id,
                        creditFacilityId:
                          wipConfig.formProps.values.creditFacilityId,
                        creditAgreement: JSON.stringify(
                          wipConfig.formProps.values,
                        ),
                      },
                    },
                  });
                }}
                ml="2"
              />
              <ResetIconButton
                ariaLabel="qa all financial covenants"
                label="QA All Financial Covenants"
                onClick={() => {
                  sendFinancialCovenantWorklease({ wipConfig, triggerDate });
                }}
                isLoading={isSendFinCovWorkleaseLoading}
              />
              {/*
               * Only exposing this QA tool on Dev since don't want to lock
               * up FinleyCore when evaluating port covs
               */}
              {commonUtils.EnvUtils.isDevelopment() && (
                <IconButton
                  ariaLabel="(dev) qa financial covenant"
                  label="(Dev) QA Financial Covenant"
                  isLoading={isEvaluateDevFinCovWorkleaseLoading}
                  onClick={() => {
                    evaluateDevFinCovWorklease({ wipConfig, triggerDate });
                  }}
                  icon={<Icon icon="dev" type="brand" />}
                />
              )}
            </Flex>
          )}

        <Flex alignItems="center" mb="2">
          <Text fontWeight="bold" fontSize="20px" mr="2">
            Properties
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              <AddIconButton
                ariaLabel="add financial covenant"
                label="Add Financial Covenant"
                onClick={() => onOpen()}
              />
              <AddFinancialCovenantModal
                isOpen={isOpen}
                onClose={onClose}
                onClickAdd={(params: {
                  nameInput: string;
                  covenantKeyInput: string;
                }) => {
                  addFinancialCovenant(params);
                }}
                initialCovenantKey="financialCovenantKey"
                initialName="Financial Covenant Name"
                mode="add"
              />
            </>
          )}
        </Flex>

        <FinancialCovenantTable
          organizationId={organizationId}
          sanitizedFinancialCovenants={financialCovenants}
          basePropertyKey={basePropertyKey}
          wipConfig={wipConfig}
          triggerDate={triggerDate}
        />
      </StickyHeaderSection>
    </Box>
  );
};
